import React, { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Card from "components/ui/Card/Card";
import Alert from "components/ui/Alert/Alert";
import { useI18n } from "context/I18nContext";
import { useAuth } from "context/AuthContext";
import {
  createRecharge,
  fetchRechargeTypes,
  type RechargeSavePayload,
  type RechargeTypeOption,
} from "utils/rechargesApi";
import { lookupAccount } from "utils/accountsApi";
import { ApiError } from "utils/apiClient";
import {
  formatDetailLabel,
  getDetailEntries,
  normaliseErrorDetail,
} from "utils/errorDetails";
import styles from "./RechargeFormPage.module.scss";

type IdentifierMode = "account" | "card";

type ResolvedDetails = {
  accountNo: string;
  customerName?: string | null;
  walletBalance?: number | null;
  cardNumber?: string | null;
  identifier: string;
};

type RouteState = {
  from?: {
    pathname?: string;
    search?: string;
  };
};

const formatCurrency = (value: number | null | undefined) => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "৳ 0.00";
  }
  return `৳ ${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const extractDetailCode = (detail: unknown): string | undefined => {
  if (!detail) {
    return undefined;
  }
  if (typeof detail === "string") {
    return detail;
  }
  if (isRecord(detail)) {
    const possibleKeys: Array<keyof typeof detail> = ["code", "error", "type"];
    for (const key of possibleKeys) {
      const candidate = detail[key];
      if (typeof candidate === "string") {
        return candidate;
      }
    }
  }
  return undefined;
};

function RechargeFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const { refreshUser, user } = useAuth();
  const routeState = location.state as RouteState | undefined;
  const backTarget = {
    pathname: routeState?.from?.pathname ?? "/recharge",
    search: routeState?.from?.search ?? "",
  };

  const pumpList = useMemo(() => user?.pumps ?? [], [user?.pumps]);

  const [rechargeTypes, setRechargeTypes] = useState<RechargeTypeOption[]>([]);
  const [, setTypesLoading] = useState(true);
  const [typesError, setTypesError] = useState<string | null>(null);

  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [referenceNo, setReferenceNo] = useState("");
  const [identifierMode, setIdentifierMode] = useState<IdentifierMode>("account");
  const [identifierValue, setIdentifierValue] = useState("");
  const [amount, setAmount] = useState("");

  const [resolvedDetails, setResolvedDetails] = useState<ResolvedDetails | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saveDetail, setSaveDetail] = useState<Record<string, unknown> | null>(null);
  const [saving, setSaving] = useState(false);
  const [postSaveLock, setPostSaveLock] = useState(false);
  const redirectTimerRef = useRef<number | null>(null);
  const alertStackRef = useRef<HTMLDivElement | null>(null);
  const initialPumpId = useMemo(() => {
    const fromSearch = routeState?.from?.search;
    const pumpFromRoute = fromSearch ? new URLSearchParams(fromSearch).get("pumpId") : null;
    const parsedRoutePump = pumpFromRoute ? Number(pumpFromRoute) : NaN;
    if (Number.isFinite(parsedRoutePump)) {
      return parsedRoutePump;
    }
    const first = pumpList[0]?.pumpId;
    return typeof first === "number" ? first : null;
  }, [pumpList, routeState?.from?.search]);
  const [selectedPumpId, setSelectedPumpId] = useState<number | null>(initialPumpId);

  useEffect(() => {
    const controller = new AbortController();
    const loadTypes = async () => {
      setTypesLoading(true);
      setTypesError(null);
      try {
        const items = await fetchRechargeTypes(controller.signal);
        setRechargeTypes(items);
      } catch (err) {
        if ((err as DOMException).name === "AbortError") {
          return;
        }
        setTypesError((err as Error).message || t("rechargeForm.errors.loadFailed"));
      } finally {
        setTypesLoading(false);
      }
    };
    loadTypes();
    return () => controller.abort();
  }, [t]);

  useEffect(
    () => () => {
      if (redirectTimerRef.current) {
        window.clearTimeout(redirectTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!(saveError || saveSuccess)) {
      return;
    }
    const element = alertStackRef.current;
    if (!element) {
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [saveError, saveSuccess]);

  const cashType = useMemo(() => {
    const decorated = rechargeTypes.map((type) => ({
      ...type,
      displayName: (type.name ?? type.transactionTypeName ?? "").toLowerCase(),
    }));
    return (
      decorated.find((type) => type.displayName.includes("cash")) ??
      decorated[0] ??
      null
    );
  }, [rechargeTypes]);

  const cashTypeId = cashType?.id ?? null;

  useEffect(() => {
    if (cashTypeId && selectedTypeId !== cashTypeId) {
      setSelectedTypeId(cashTypeId);
    }
  }, [cashTypeId, selectedTypeId]);

  const numericAmount = Number(amount);
  const hasValidAmount = Number.isFinite(numericAmount) && numericAmount > 0;
  const isValidTypeSelection =
    Boolean(selectedTypeId) && (!cashTypeId || selectedTypeId === cashTypeId);
  const disableSubmit =
    saving ||
    postSaveLock ||
    !resolvedDetails ||
    !hasValidAmount ||
    !isValidTypeSelection ||
    (pumpList.length > 0 && selectedPumpId === null);

  const handleBackNavigation = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(backTarget);
    }
  };

  useEffect(() => {
    if (selectedPumpId === null && initialPumpId !== null) {
      setSelectedPumpId(initialPumpId);
    }
  }, [initialPumpId, selectedPumpId]);

  const handleIdentifierModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextMode = event.target.value as IdentifierMode;
    setIdentifierMode(nextMode);
    setIdentifierValue("");
    setResolvedDetails(null);
    setLookupError(null);
  };

  const handleIdentifierChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIdentifierValue(event.target.value);
    setResolvedDetails(null);
    setLookupError(null);
  };

  const handleVerify = async () => {
    const value = identifierValue.trim();
    if (!value) {
      setLookupError(t("rechargeForm.errors.identifierRequired"));
      setResolvedDetails(null);
      return;
    }

    setLookupError(null);
    setLookupLoading(true);

    const controller = new AbortController();
    try {
      const lookup = await lookupAccount({
        accountNo: identifierMode === "account" ? value : undefined,
        cardNumber: identifierMode === "card" ? value : undefined,
        signal: controller.signal,
      });
      const accountNo = lookup.account?.accountNo ?? "";
      const primaryCard = lookup.cards?.find((card) => card.cardNumber) ?? null;
      if (!accountNo) {
        throw new Error(t("rechargeForm.errors.lookupFailed"));
      }
      const resolvedIdentifier =
        identifierMode === "card"
          ? primaryCard?.cardNumber ?? value
          : accountNo;
      setResolvedDetails({
        accountNo,
        customerName:
          lookup.customer?.customerName ??
          primaryCard?.cardNumber ??
          accountNo ??
          value,
        walletBalance: lookup.account?.walletBalance ?? null,
        cardNumber: primaryCard?.cardNumber ?? undefined,
        identifier: resolvedIdentifier,
      });
    } catch (err) {
      const apiError = err instanceof ApiError ? err : null;
      const baseMessage =
        apiError?.message ||
        (err as Error).message ||
        t("rechargeForm.errors.lookupFailed");

      let translatedMessage = baseMessage;
      const detailCode = extractDetailCode(apiError?.detail);
      if (detailCode === "card_not_linked") {
        translatedMessage = t("rechargeForm.errors.cardNotLinked");
      } else if (detailCode === "account_has_no_card") {
        translatedMessage = t("rechargeForm.errors.accountHasNoCard");
      } else if (baseMessage?.toLowerCase().includes("card not found")) {
        translatedMessage = t("rechargeForm.errors.lookupCardNotFound");
      } else if (baseMessage?.toLowerCase().includes("account not found")) {
        translatedMessage = t("rechargeForm.errors.lookupAccountNotFound");
      } else if (baseMessage?.toLowerCase().includes("required")) {
        translatedMessage = t("rechargeForm.errors.lookupMissingParams");
      } else {
        translatedMessage = t("rechargeForm.errors.lookupFailed");
      }

      setLookupError(translatedMessage);
      setResolvedDetails(null);
    } finally {
      setLookupLoading(false);
    }
  };

  const renderDetail = (detail: Record<string, unknown> | null) => {
    const entries = getDetailEntries(detail);
    if (!entries.length) {
      return null;
    }
    return (
      <dl className={styles.detailList}>
        {entries.map(([key, value]) => (
          <div key={key} className={styles.detailRow}>
            <dt>{formatDetailLabel(key)}</dt>
            <dd>{String(value)}</dd>
          </div>
        ))}
      </dl>
    );
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaveError(null);
    setSaveSuccess(null);
    setSaveDetail(null);

    const numericAmount = Number(amount);
    if (!resolvedDetails) {
      setSaveError(t("rechargeForm.errors.identifierRequired"));
      return;
    }
    if (!selectedTypeId || (cashTypeId && selectedTypeId !== cashTypeId)) {
      setSaveError(t("rechargeForm.errors.typeUnavailable"));
      return;
    }
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setSaveError(t("rechargeForm.errors.invalidAmount"));
      return;
    }

    setSaving(true);
    setPostSaveLock(false);

    const payload: RechargeSavePayload = {
      transactionTypeId: selectedTypeId,
      amount: numericAmount,
      referenceNo: referenceNo.trim() || undefined,
      accountNo: identifierMode === "account" ? resolvedDetails.identifier : undefined,
      cardNumber: identifierMode === "card" ? resolvedDetails.identifier : undefined,
      pumpId: selectedPumpId ?? undefined,
    };

    try {
      await createRecharge(payload);
      await refreshUser().catch(() => undefined);
      setSaveSuccess(t("rechargeForm.messages.createSuccessRedirect"));
      setPostSaveLock(true);
      redirectTimerRef.current = window.setTimeout(() => {
        const nextSearch = new URLSearchParams(backTarget.search);
        if (selectedPumpId) {
          nextSearch.set("pumpId", String(selectedPumpId));
        }
        const searchString = nextSearch.toString();
        navigate(
          `${backTarget.pathname}${searchString ? `?${searchString}` : ""}`,
          {
            replace: true,
            state: {
              flash: {
                variant: "success",
                message: t("rechargeForm.flash.createSuccess"),
              },
            },
          }
        );
      }, 1600);
    } catch (err) {
      const apiError = err instanceof ApiError ? err : null;
      const detailCode = extractDetailCode(apiError?.detail);
      let detailMessage: string | null = null;
      if (detailCode === "card_not_linked") {
        detailMessage = t("rechargeForm.errors.cardNotLinked");
      } else if (detailCode === "account_has_no_card") {
        detailMessage = t("rechargeForm.errors.accountHasNoCard");
      } else if (detailCode === "pump_access_denied") {
        detailMessage = t("rechargeForm.errors.pumpAccessDenied");
      } else if (detailCode === "pump_insufficient_balance") {
        detailMessage = t("rechargeForm.errors.pumpInsufficientBalance");
      } else if (detailCode === "pump_operator_only") {
        detailMessage = t("rechargeForm.errors.pumpOperatorOnly");
      } else if (detailCode === "account_not_active") {
        detailMessage = t("rechargeForm.errors.accountNotActive");
      } else if (detailCode === "account_has_no_active_card") {
        detailMessage = t("rechargeForm.errors.accountHasNoActiveCard");
      } else if (
        identifierMode === "card" &&
        (detailCode === "card_not_active" || detailCode === "card_expired")
      ) {
        detailMessage = t("rechargeForm.errors.cardInactiveOrExpired");
      }
      const message =
        detailMessage ||
        apiError?.message ||
        (err as Error).message ||
        t("rechargeForm.errors.saveFailed");
      setSaveError(message);
      setSaveDetail(normaliseErrorDetail(apiError?.detail));
      setPostSaveLock(false);
    } finally {
      setSaving(false);
    }
  };

  const formId = "recharge-create";
  const pageTitle = t("rechargeForm.title");
  const pageSubtitle = t("rechargeForm.subtitle");
  const primaryActionLabel = saving
    ? t("rechargeForm.buttons.creating")
    : t("rechargeForm.buttons.create");
  const backLabel = t("rechargeForm.buttons.back");
  const cancelLabel = t("rechargeForm.buttons.cancel");

  return (
    <div className={styles.page}>
      <Card>
        <div className={styles.header}>
          <div className={styles.intro}>
            <h1 className={styles.title}>{pageTitle}</h1>
            <p className={styles.subtitle}>{pageSubtitle}</p>
          </div>
          <button
            type="button"
            className={`${styles.backButton} ${styles.backButtonSmall}`}
            onClick={handleBackNavigation}
            disabled={saving}
          >
            {backLabel}
          </button>
        </div>

        {(saveError || saveSuccess) && (
          <div ref={alertStackRef} className={styles.alertStack}>
            {saveError && (
              <Alert
                variant="error"
                dismissible
                onClose={() => setSaveError(null)}
              >
                <div>{saveError}</div>
                {renderDetail(saveDetail)}
              </Alert>
            )}
            {saveSuccess && (
              <Alert
                variant="success"
                dismissible={!postSaveLock}
                onClose={
                  postSaveLock ? undefined : () => setSaveSuccess(null)
                }
              >
                {saveSuccess}
              </Alert>
            )}
          </div>
        )}

        {typesError && (
          <div className={styles.alertStack}>
            <Alert variant="error" dismissible onClose={() => setTypesError(null)}>
              {typesError}
            </Alert>
          </div>
        )}

        <form id={formId} className={styles.form} onSubmit={handleSubmit}>
          <fieldset className={styles.fieldset} disabled={saving || postSaveLock}>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>{t("rechargeForm.sections.transaction")}</h2>

              {pumpList.length > 0 && (
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="pumpId">
                    {t("rechargeForm.fields.pump")}
                  </label>
                  <select
                    id="pumpId"
                    className={styles.select}
                    value={selectedPumpId ?? ""}
                    onChange={(event) =>
                      setSelectedPumpId(event.target.value ? Number(event.target.value) : null)
                    }
                  >
                    <option value="">{t("rechargeForm.placeholders.pump")}</option>
                    {pumpList.map((pump) => (
                      <option key={pump.pumpId} value={pump.pumpId}>
                        {pump.pumpName}
                      </option>
                    ))}
                  </select>
                  {selectedPumpId !== null && (
                    <div className={styles.pumpBalance}>
                      <span className={styles.pumpBalanceLabel}>
                        {t("rechargePage.pumpSummary.balance")}
                      </span>
                      <span className={styles.pumpBalanceValue}>
                        {formatCurrency(
                          Number(
                            pumpList.find((pump) => pump.pumpId === selectedPumpId)?.pumpBalance ??
                              0
                          )
                        )}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className={styles.field}>
                <span className={styles.label}>
                  {t("rechargeForm.fields.identifierMode")}
                </span>
                <div className={styles.toggleGroup}>
                  <label className={styles.toggle}>
                    <input
                      type="radio"
                      name="identifierMode"
                      value="account"
                      checked={identifierMode === "account"}
                      onChange={handleIdentifierModeChange}
                    />
                    {t("rechargeForm.identifierModes.account")}
                  </label>
                  <label className={styles.toggle}>
                    <input
                      type="radio"
                      name="identifierMode"
                      value="card"
                      checked={identifierMode === "card"}
                      onChange={handleIdentifierModeChange}
                    />
                    {t("rechargeForm.identifierModes.card")}
                  </label>
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="identifierValue">
                  {identifierMode === "account"
                    ? t("rechargeForm.fields.accountNo")
                    : t("rechargeForm.fields.cardNumber")}
                </label>
                <div className={styles.inlineField}>
                  <input
                    id="identifierValue"
                    className={styles.input}
                    value={identifierValue}
                    onChange={handleIdentifierChange}
                    placeholder={
                      identifierMode === "account"
                        ? t("rechargeForm.placeholders.accountNo")
                        : t("rechargeForm.placeholders.cardNumber")
                    }
                  />
                  <button
                    type="button"
                    className={styles.verifyButton}
                    onClick={handleVerify}
                    disabled={!identifierValue.trim() || lookupLoading}
                  >
                    {lookupLoading
                      ? t("rechargeForm.buttons.verifying")
                      : t("rechargeForm.buttons.verify")}
                  </button>
                </div>
                {lookupError && <p className={styles.pageError}>{lookupError}</p>}
              </div>

              {resolvedDetails && (
                <div className={styles.previewCard}>
                  <div>
                    <span className={styles.previewLabel}>
                      {t("rechargeForm.info.resolvedCustomer")}
                    </span>
                    <span className={styles.previewValue}>
                      {resolvedDetails.customerName ?? "—"}
                    </span>
                  </div>
                  <div>
                    <span className={styles.previewLabel}>
                      {t("rechargeForm.info.resolvedAccount")}
                    </span>
                    <span className={styles.previewValue}>
                      {resolvedDetails.accountNo || "—"}
                    </span>
                  </div>
                  <div>
                    <span className={styles.previewLabel}>
                      {t("rechargeForm.info.resolvedBalance")}
                    </span>
                    <span className={styles.previewValue}>
                      {formatCurrency(resolvedDetails.walletBalance)}
                    </span>
                  </div>
                </div>
              )}

              <div className={styles.field}>
                <label className={styles.label} htmlFor="referenceNo">
                  {t("rechargeForm.fields.referenceNo")}
                </label>
                <input
                  id="referenceNo"
                  className={styles.input}
                  value={referenceNo}
                  onChange={(event) => setReferenceNo(event.target.value)}
                  placeholder={t("rechargeForm.placeholders.referenceNo")}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="amount">
                  {t("rechargeForm.fields.amount")}
                </label>
                <input
                  id="amount"
                  className={styles.input}
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  placeholder={t("rechargeForm.placeholders.amount")}
                />
              </div>
            </section>
          </fieldset>

          <div className={styles.formActions}>
            <div className={styles.formActionsRight}>
              <button
                type="button"
                className={styles.backButton}
                onClick={handleBackNavigation}
                disabled={saving}
              >
                {cancelLabel}
              </button>
              <button
                type="submit"
                className={styles.primaryButton}
                disabled={disableSubmit}
              >
                {primaryActionLabel}
              </button>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
}

export default RechargeFormPage;
